import { OrgAddressResponse, OrgAddressType, OrgId, OrgResponse } from '@business/entities/organization';
import { CommonService } from '@business/services';
import { Button } from '@core/components/Button';
import { classnames } from '@core/utils/css';
import { useObservable } from '@core/utils/hooks/rxjs';
import { R } from '@core/utils/r';
import React, { memo, useCallback, useMemo, useState, Fragment } from 'react';
import { Card, CardBody, CardTitle, Col, Collapse, Row } from 'reactstrap';
import { openAddEditAddress } from '../../AddEditAddressModal';
import { OrganizationPageBloc } from '../../OrganizationPageBloc';
import './index.scss';
import { useSettings } from '@core/utils/hooks/misc/useSettings';
import { Formatter } from '@core/utils/formatter';

interface AddressCardProps {
  address: OrgAddressResponse;
  onDelete(id: string): void;
  onDefault(id: string): void;
  onUpdate(address: OrgAddressResponse): void;
  showTypeLabel?: boolean;
}

const AddressCard = memo((props: AddressCardProps) => {
  const { address, showTypeLabel = false } = props;
  const settings = useSettings();

  // Extract country code from address extra data for phone formatting
  const phoneCountryCode = useMemo(() => {
    return address.extra?.countryCode || 'us';
  }, [address.extra]);

  return (
    <Card style={{ border: '1px solid var(--input-border-color)' }}>
      <CardBody>
        <div className="d-flex justify-content-between">
          <div className="address mb-3">
            {!R.isEmpty(address.name) && (
              <Fragment>
                <span className="font-weight-medium">{address.name}</span>
                <br />
              </Fragment>
            )}
            <span>{address.address}</span>
            <br />
            {!R.isEmpty(address.addressLineTwo) && (
              <React.Fragment>
                <span>{address.addressLineTwo}</span>
                <br />
              </React.Fragment>
            )}
            <span>
              {address.city}, {address.state} {address.postal}
            </span>
            <br />
            <span>{address.country}</span>
            {!R.isEmpty(address.phone) && (
              <Fragment>
                <br />
                <span>{Formatter.formatPhone(address.phone, phoneCountryCode)}</span>
              </Fragment>
            )}
          </div>
          {showTypeLabel && (
            <span className={classnames(`align-self-start badge py-1 px-2 badge-${address.type !== OrgAddressType.both ? 'success' : 'info'}`)}>
              {CommonService.getAddressTypeLabel(address.type)}
            </span>
          )}
        </div>
        <div className="address_card-footer">
          <span className="controls">
            <a className={classnames('card-link')} onClick={() => props.onUpdate(address)}>
              <i className="bx bx-edit font-size-16"></i>
            </a>

            <a className={classnames('card-link', 'text-danger')} onClick={() => props.onDelete(address.id)}>
              <i className="bx bx-trash font-size-16 text-danger"></i>
            </a>
            {!!settings?.allowContactAddress && address.contact && (
              <span className={classnames('badge badge-success ml-2')}>{CommonService.getFullName(address.contact)}</span>
            )}
          </span>
          {!address.isDefault && (
            <a className={classnames('card-link btn-link')} onClick={() => props.onDefault(address.id)} style={{ lineHeight: 1 }}>
              Make Default
            </a>
          )}
          {address.isDefault && <span className={classnames('badge badge-pill badge-info')}>Default</span>}
        </div>
      </CardBody>
    </Card>
  );
});

export interface AddressesProps {
  bloc: OrganizationPageBloc;
  orgId: OrgId;
  ordersActive?: boolean;
}
export const Addresses = memo((props: AddressesProps) => {
  const { bloc, orgId, ordersActive = false } = props;
  const organization = useObservable(bloc.organization$) as OrgResponse;
  const showTypeLabel = useMemo(() => ordersActive, [ordersActive]);

  const addresses = useMemo(() => {
    if (!organization) return [];
    return organization.addresses;
  }, [organization]);

  const onDelete = useCallback(
    (addressId: string) => {
      bloc.deleteAddress(addressId, orgId).subscribe();
    },
    [bloc, orgId],
  );

  const onPrimary = useCallback(
    (addressId: string) => {
      bloc.makeAddressPrimary(addressId, orgId).subscribe();
    },
    [bloc, orgId],
  );
  const [isOpen, setOpen] = useState(true);
  return (
    <Card>
      <CardBody>
        <CardTitle className="d-flex align-items-center justify-content-between py-2">
          <span className="d-flex">
            Addresses
            <a className="ml-2 d-flex" onClick={() => setOpen(!isOpen)}>
              <i className={`bx bx-chevron-${isOpen ? 'up' : 'down'} font-size-22`}></i>
            </a>
          </span>
          <Button intent="primary" onClick={() => openAddEditAddress({ orgId: orgId, isFirst: addresses.length === 0 })}>
            Add Address
          </Button>
        </CardTitle>
        <Collapse isOpen={isOpen}>
          <div className="pt-2">
            {R.chunk(addresses, 2).map(twoAddresses => {
              const firstAddress = R.first(twoAddresses);
              const secondAddress = R.get(twoAddresses, 1);

              return (
                <Row key={firstAddress?.id}>
                  <Col lg={6}>
                    <AddressCard
                      address={firstAddress!}
                      onDelete={onDelete}
                      onDefault={onPrimary}
                      onUpdate={address => openAddEditAddress({ address, orgId })}
                      showTypeLabel={showTypeLabel}
                    />
                  </Col>
                  {!!secondAddress && (
                    <Col lg={6}>
                      <AddressCard
                        address={secondAddress}
                        onDelete={onDelete}
                        onDefault={onPrimary}
                        onUpdate={address => openAddEditAddress({ address, orgId })}
                        showTypeLabel={showTypeLabel}
                      />
                    </Col>
                  )}
                </Row>
              );
            })}
          </div>
        </Collapse>
      </CardBody>
    </Card>
  );
});
